import { useState, useCallback, useMemo, memo, ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';

import { cn, TabComponentType, useAnalytics } from '@restworld/utils-common';
import { HandRaisedIcon } from '@heroicons/react/24/outline';
import { TermsAndConditions } from '../../../legacy';
import { Heading, Button } from '@restworld/ui-ds-base';

import { useEntryPoint } from '../../worker-entry-point.hooks';
import { useEntryPointCheckListContext } from './entry-component.context';
import { useCVLoadingSliderActiveIndex } from '../../worker-cvloading-ui';
import { activeTabAtom, spontaneousApplyDataAtom, uploadFilesAtom } from '../../worker-entry-point.atoms';
import { jobPositionApiDataAtom } from '../../../job-position/job-position.atoms';
import { entryPointModalVisibleAtom } from '../../worker-entry-point-modal';

export const LoginPrivacyRedirectSubmitButton = ({ type }: { type: TabComponentType }) => {
  const [isPrivacyChecked, setIsPrivacyChecked] = useState(false);

  const { onUploadFileChange } = useEntryPoint();
  const uploadFiles = useAtomValue(uploadFilesAtom);
  const [spontaneousApplyData, setSpontaneousApplyData] = useAtom(spontaneousApplyDataAtom);
  // const { fbTrackWorkerStartsApplication } = useAnalytics();
  const { setBtnTriggeredWithoutCheck, setIsCVNotUploaded } = useEntryPointCheckListContext();
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const jobPositionApiData = useAtomValue(jobPositionApiDataAtom);
  const toggleEntryPoint = useSetAtom(entryPointModalVisibleAtom);

  const query = router?.['query'];
  const { onSlideChanged } = useCVLoadingSliderActiveIndex();
  const onClick = useCallback(() => {
    if (!isPrivacyChecked) setBtnTriggeredWithoutCheck(true);
    if (type === 'upload-cv' && !uploadFiles?.[0]) setIsCVNotUploaded(true);
    if (type === 'upload-cv' && uploadFiles?.[0] && isPrivacyChecked) {
      onSlideChanged(0);
      onUploadFileChange();
      setSpontaneousApplyData((p) => ({ ...p, entryType: type }));
      trackEvent('upload_cv', {
        upload_media: true,
        close_button: false,
      });
    }
    if (type === 'application-wizard' && isPrivacyChecked) {
      trackEvent('initiate_questionnaire', {
        close_button: true,
        terms_and_conditions: true,
      });
      // fbTrackWorkerStartsApplication((query['id'] ?? query['slug'] ?? '') as string);
      setSpontaneousApplyData((p) => ({ ...p, entryType: type }));
      const querySlug = query['slug'] ? `${query['slug']}` : '';
      router?.push(
        `/candidati/${querySlug}${
          spontaneousApplyData?.isSpontaneousEntry
            ? `?isSpontaneousEntry=${spontaneousApplyData?.isSpontaneousEntry}`
            : ''
        }${
          jobPositionApiData?.manage_using_klaaryo
            ? `${spontaneousApplyData?.isSpontaneousEntry ? '&' : '?'}manage_using_klaaryo=true`
            : ''
        }`
      );
      toggleEntryPoint(false);
    }
  }, [
    isPrivacyChecked,
    setBtnTriggeredWithoutCheck,
    type,
    uploadFiles,
    setIsCVNotUploaded,
    onSlideChanged,
    onUploadFileChange,
    setSpontaneousApplyData,
    trackEvent,
    query,
    router,
    spontaneousApplyData?.isSpontaneousEntry,
    jobPositionApiData?.manage_using_klaaryo,
    toggleEntryPoint,
  ]);

  const { t } = useTranslation();
  return (
    <Wrapper>
      <EntryPointTermsAndConditions
        testId="entry-point-terms-and-conditions"
        setIsChecked={setIsPrivacyChecked}
        isChecked={isPrivacyChecked}
        type={type}
      />
      <Button
        variant="primary"
        size="md"
        onClick={onClick}
        fullwidth
        testId={type === 'upload-cv' ? 'upload-cv-button' : 'onboarding-button'}
      >
        {type === 'upload-cv' ? t('common:upload_cv') : t('onboarding-page:fill_out_the_questionnaire')}
      </Button>
      <WarningText type={type} />
      <RedirectButtonComponent type={type} />
    </Wrapper>
  );
};
const EntryPointTermsAndConditions = ({
  type,
  isChecked,
  setIsChecked,
  testId,
}: {
  type: TabComponentType;
  isChecked: boolean;
  setIsChecked: Dispatch<SetStateAction<boolean>>;
  testId?: string;
}) => {
  const { isBtnTriggeredWithoutCheck, setBtnTriggeredWithoutCheck } = useEntryPointCheckListContext();
  const onCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) setBtnTriggeredWithoutCheck(false);
      setIsChecked(event.target.checked);
    },
    [setBtnTriggeredWithoutCheck, setIsChecked]
  );
  return (
    <TermsAndConditions
      key={type}
      onCheckboxChange={onCheckboxChange}
      isChecked={isChecked}
      type={type}
      iserror={isBtnTriggeredWithoutCheck}
      testId={testId}
    />
  );
};
const WarningText = memo(({ type }: { type: TabComponentType }) => {
  const { t } = useTranslation('onboarding-page');
  const { isBtnTriggeredWithoutCheck, isCVNotUploaded } = useEntryPointCheckListContext();
  const textContent = isBtnTriggeredWithoutCheck
    ? type === 'application-wizard'
      ? t('accept_the_terms_and_conditions_to_continue')
      : t('upload_acv_and_accept_the_terms_and_conditions_to_continue')
    : t('upload_acv_to_continue');

  return isBtnTriggeredWithoutCheck || isCVNotUploaded ? (
    <span className="flex flex-row items-center gap-1.5 justify-center">
      <HandRaisedIcon className="h-4 w-4 text-red-500" />
      <Heading size={6} color="error" weight="normal" testId="onboarding-warning-text">
        {textContent}
      </Heading>
    </span>
  ) : null;
});
const RedirectButtonComponent = memo(({ type }: { type: TabComponentType }) => {
  const { t } = useTranslation('onboarding-page');
  const { setBtnTriggeredWithoutCheck, setIsCVNotUploaded } = useEntryPointCheckListContext();
  const setActiveTab = useSetAtom(activeTabAtom);
  const onTogglerCallback = useCallback(() => {
    setBtnTriggeredWithoutCheck(false);
    setIsCVNotUploaded(false);
    setActiveTab(type === 'upload-cv' ? 'application-wizard' : 'upload-cv');
  }, [setActiveTab, setBtnTriggeredWithoutCheck, setIsCVNotUploaded, type]);
  const togglerButtonTitle = useMemo(
    () => (type === 'upload-cv' ? t('fill_out_the_questionnaire') : t('upload_it')),
    [t, type]
  );
  const titleContent = useMemo(
    () => (type === 'upload-cv' ? t('you_dont_have_cv') : t('do_you_already_have_acv')),
    [t, type]
  );
  return (
    <TogglerWrapper>
      <Heading size={6} color="muted" weight="light">
        {titleContent}
      </Heading>
      <Button variant="ghost" size="sm" onClick={onTogglerCallback}>
        {togglerButtonTitle}
      </Button>
    </TogglerWrapper>
  );
});
const TogglerWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  // items-center so the question label and the "Load it" toggle button share a baseline
  <span className={cn('flex gap-1 mx-auto items-center', className)} {...props} />
);
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4', className)} {...props} />
);
